import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { useTracking } from './TrackingContext';
import { trackAddToCart, trackInitiateCheckout, formatProductData, formatCartData } from '../utils/metaPixel';

const CartContext = createContext();

// Default cart context value to prevent destructuring errors
const defaultCartValue = {
  items: [],
  itemCount: 0,
  totalValue: 0,
  loading: false,
  error: null,
  isInitialized: false,
  addToCart: () => Promise.resolve(false),
  updateCartItem: () => Promise.resolve(false),
  removeFromCart: () => Promise.resolve(false),
  clearCart: () => Promise.resolve(false),
  isInCart: () => false,
  getCartItem: () => null,
  formatPrice: (price) => price?.toString() || '0',
  fetchCart: () => Promise.resolve(),
  cleanupDeletedProducts: () => Promise.resolve(false),
  hasDeletedProducts: () => false
};

// Action types
const ADD_TO_CART = 'ADD_TO_CART';
const UPDATE_CART_ITEM = 'UPDATE_CART_ITEM';
const REMOVE_CART_ITEM = 'REMOVE_CART_ITEM';
const CLEAR_CART = 'CLEAR_CART';
const SET_CART = 'SET_CART';
const CART_ERROR = 'CART_ERROR';

// A product counts as available only when it's active AND actually has stock.
// Checking raw stock (not just the status label) guards against the two ever
// drifting out of sync (e.g. stock edited without the status field updating).
const isProductAvailable = (product) => {
  if (product.status !== 'active') return false;
  if (product.stock !== undefined && product.stock !== null && product.stock <= 0) return false;
  return true;
};

// Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case SET_CART:
      const validItems = filterValidCartItems(action.payload.items || []);
      const availableItemsSet = validItems.filter(item => isProductAvailable(item.product));
      const waitlistItemsSet = validItems.filter(item => !isProductAvailable(item.product));
      return {
        ...state,
        items: validItems,
        availableItems: availableItemsSet,
        waitlistItems: waitlistItemsSet,
        itemCount: availableItemsSet.reduce((total, item) => total + item.quantity, 0),
        totalValue: calculateCartTotal(availableItemsSet),
        loading: false,
        error: null
      };
    case ADD_TO_CART:
      return {
        ...state,
        loading: false,
        error: null
      };
    case UPDATE_CART_ITEM:
      return {
        ...state,
        loading: false,
        error: null
      };
    case REMOVE_CART_ITEM:
      return {
        ...state,
        loading: false,
        error: null
      };
    case CLEAR_CART:
      return {
        ...state,
        items: [],
        availableItems: [],
        waitlistItems: [],
        itemCount: 0,
        totalValue: 0,
        loading: false,
        error: null
      };
    case CART_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    default:
      return state;
  }
};

// Guest (logged-out) cart is kept entirely client-side in localStorage, in
// the same shape SET_CART already expects ({_id, product, quantity,
// attributes}), so the existing reducer/rendering logic works unchanged for
// both guests and logged-in users. It's merged into the real account cart
// once the guest logs in (see the mergeGuestCartThenFetch effect below).
const GUEST_CART_KEY = 'guestCart';

const loadGuestCart = () => {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items : [];
  } catch (error) {
    console.warn('Failed to read guest cart from storage:', error);
    return [];
  }
};

const saveGuestCart = (items) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch (error) {
    console.warn('Failed to save guest cart to storage:', error);
  }
};

// Helper function to filter out deleted/invalid products from cart items
const filterValidCartItems = (items) => {
  if (!Array.isArray(items)) return [];
  
  return items.filter(item => {
    // Keep only items that have valid product data
    return item && 
           item.product && 
           item.product._id && 
           item.product.name && 
           (item.product.regularPrice || item.product.regularPrice === 0);
  });
};

// Helper function to calculate cart total
const calculateCartTotal = (items) => {
  const validItems = filterValidCartItems(items);
  const availableItems = validItems.filter(item => isProductAvailable(item.product));
  
  return availableItems.reduce((total, item) => {
    const price = getProductPrice(item.product);
    return total + (price * item.quantity);
  }, 0);
};

// Helper function to get the effective price of a product
const getProductPrice = (product) => {
  if (!product) return 0;

  const now = new Date();
  
  // Check if near expiry is active and has expiry date
  if (product.isNearExpiry && product.expiryDate && product.nearExpiryDiscountValue) {
    const expiryDate = new Date(product.expiryDate);
    if (now <= expiryDate) {
      if (product.nearExpiryDiscountType === 'percentage') {
        return Math.round(product.regularPrice * (1 - product.nearExpiryDiscountValue / 100));
      } else {
        return product.regularPrice - product.nearExpiryDiscountValue;
      }
    }
  }

  // Check if sale is active and within date range
  const isSaleActive = product.isSaleActive && 
    product.discountPercentage && 
    product.saleStartDate && 
    product.saleEndDate &&
    now >= new Date(product.saleStartDate) && 
    now <= new Date(product.saleEndDate);

  // Sale discount takes priority if active
  if (isSaleActive) {
    const salePrice = product.regularPrice * (1 - product.discountPercentage / 100);
    return Math.round(salePrice);
  }

  // Fall back to special offer price
  if (product.specialOfferPrice && product.specialOfferPrice < product.regularPrice) {
    return product.specialOfferPrice;
  }

  // Fall back to discount calculation
  if (product.discountValue > 0 && product.regularPrice) {
    if (product.discountType === 'percentage') {
      return product.regularPrice * (1 - product.discountValue / 100);
    } else {
      return product.regularPrice - product.discountValue;
    }
  }

  // Default to regular price
  return product.regularPrice || 0;
};

export const CartProvider = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const { trackEvent } = useTracking();
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    availableItems: [],
    waitlistItems: [],
    itemCount: 0,
    totalValue: 0,
    loading: false,
    error: null
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper function to check if cart has any deleted products
  const hasDeletedProducts = () => {
    try {
      if (!state.items || !Array.isArray(state.items)) {
        return false;
      }
      const validItems = filterValidCartItems(state.items);
      return state.items.length > validItems.length;
    } catch (error) {
      console.warn('Error checking for deleted products:', error);
      return false;
    }
  };

  // Fetch cart on mount and when auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      const pendingGuestItems = loadGuestCart();
      if (pendingGuestItems.length > 0) {
        mergeGuestCartThenFetch(pendingGuestItems);
      } else {
        fetchCart();
      }
    } else {
      // Logged out (or not logged in yet): show whatever's in the local
      // guest cart instead of clearing it, so items a guest added persist
      // across page reloads until they log in and it gets merged.
      dispatch({ type: SET_CART, payload: { items: loadGuestCart() } });
    }
    setIsInitialized(true);
  }, [isAuthenticated]);

  // Periodic cleanup check - run every 5 minutes when user is authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const cleanupInterval = setInterval(() => {
      try {
        // Only run if we have items and some might be invalid
        if (state.items.length > 0 && hasDeletedProducts()) {
          // Silent cleanup without showing toast
          cleanupDeletedProducts().catch(error => {
            console.warn('Periodic cleanup failed:', error);
          });
        }
      } catch (error) {
        console.warn('Error in periodic cleanup check:', error);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(cleanupInterval);
  }, [isAuthenticated]); // Removed state.items.length from dependencies

  const fetchCart = async () => {
    // The cart API is auth-only; guests are served their local cart by the
    // mount/auth-change effect above instead. Some callers (e.g. CartPage's
    // own mount effect) call fetchCart directly without checking auth first,
    // so guard here too rather than relying on every caller to check.
    if (!isAuthenticated) return;

    try {
      const response = await api.get('/cart');
      const cartData = response.data.data;
      
      // Filter out deleted/invalid products
      const validItems = filterValidCartItems(cartData.items || []);
      const originalItemCount = cartData.items?.length || 0;
      const validItemCount = validItems.length;
      
      // If some items were filtered out, we need to clean up the cart on the server
      if (originalItemCount > validItemCount && validItemCount >= 0) {
        
        // If there are deleted products, clean up the cart silently
        if (originalItemCount > validItemCount) {
          try {
            await cleanupCartOnServer(validItems);
          } catch (cleanupError) {
            console.warn('Could not clean up cart on server:', cleanupError);
            // Don't fail the whole operation if cleanup fails
          }
        }
      }
      
      dispatch({ 
        type: SET_CART, 
        payload: {
          ...cartData,
          items: validItems
        }
      });
    } catch (error) {
      console.error('Error fetching cart:', error);
      dispatch({ 
        type: CART_ERROR, 
        payload: error.response?.data?.message || 'Failed to load cart' 
      });
    }
  };

  // Merge a guest's locally-stored cart into their real account cart right
  // after they log in, then fetch the authoritative merged cart. The backend
  // already sums quantities for a product already in the account cart, so
  // this is just a sequence of the normal add-to-cart calls. Items that fail
  // to merge (e.g. a network hiccup) are kept in the local guest cart so
  // they're retried on the next login instead of silently lost.
  const mergeGuestCartThenFetch = async (guestItems) => {
    const stillPending = [];
    let mergedCount = 0;

    for (const item of guestItems) {
      const productId = item.product?._id;
      if (!productId) continue;
      try {
        await api.post('/cart', {
          productId,
          quantity: item.quantity,
          attributes: item.attributes || {}
        });
        mergedCount++;
      } catch (error) {
        console.warn('Failed to merge guest cart item into account cart:', productId, error);
        stillPending.push(item);
      }
    }

    saveGuestCart(stillPending);

    if (mergedCount > 0) {
      toast.success(`${mergedCount} item${mergedCount > 1 ? 's' : ''} from your cart ${mergedCount > 1 ? 'were' : 'was'} saved to your account`);
    }

    await fetchCart();
  };

  // Helper function to clean up cart on server by removing invalid items
  const cleanupCartOnServer = async (validItems) => {
    try {
      // Send a request to update the cart with only valid items
      await api.put('/cart/cleanup', {
        items: validItems.map(item => ({
          productId: item.product._id,
          quantity: item.quantity,
          attributes: item.attributes || {}
        }))
      });
    } catch (error) {
      // If the cleanup endpoint doesn't exist, try alternative approach
      if (error.response?.status === 404) {
        // We could implement individual removal calls here if needed
        // For now, just log the issue
      } else {
        throw error;
      }
    }
  };

  // Guests add to a cart kept in localStorage instead of hitting the (auth-only)
  // cart API. It's merged into their real cart automatically once they log in.
  const addToGuestCart = async (productId, quantity, attributes) => {
    try {
      const response = await api.get(`/products/${productId}`);
      const product = response.data.data;

      if (!product) {
        toast.error('Failed to add item to cart');
        return false;
      }

      const existing = loadGuestCart();
      const existingIndex = existing.findIndex(item => item.product?._id === productId);

      const updatedItems = existingIndex > -1
        ? existing.map((item, idx) =>
            idx === existingIndex ? { ...item, quantity: item.quantity + quantity } : item
          )
        : [...existing, { _id: productId, product, quantity, attributes }];

      saveGuestCart(updatedItems);

      dispatch({ type: ADD_TO_CART });
      dispatch({
        type: SET_CART,
        payload: { items: filterValidCartItems(updatedItems) }
      });

      // Track AddToCart event with Meta Pixel (same as the logged-in path)
      try {
        const productData = formatProductData(product);
        productData.value = productData.value * quantity;
        trackAddToCart(productData);
        trackEvent('ADD_TO_CART', productId, 'Product', { quantity, attributes });
      } catch (trackingError) {
        console.warn('Meta Pixel tracking failed:', trackingError);
        trackEvent('ADD_TO_CART', productId, 'Product', { quantity, attributes });
      }

      toast.success('Item added to cart');
      return true;
    } catch (error) {
      console.error('Error adding to guest cart:', error);
      toast.error('Failed to add item to cart');
      return false;
    }
  };

  const addToCart = async (productId, quantity = 1, attributes = {}) => {
    if (!isAuthenticated) {
      return addToGuestCart(productId, quantity, attributes);
    }

    try {
      const response = await api.post('/cart', {
        productId,
        quantity,
        attributes
      });

      dispatch({ 
        type: ADD_TO_CART
      });
      
      dispatch({ 
        type: SET_CART, 
        payload: {
          ...response.data.data,
          items: filterValidCartItems(response.data.data.items || [])
        }
      });
      
      // Track AddToCart event with Meta Pixel
      try {
        const addedItem = response.data.data.items?.find(item => 
          item.product?._id === productId || item.product === productId
        );
        if (addedItem && addedItem.product) {
          const productData = formatProductData(addedItem.product);
          productData.value = productData.value * quantity; // Multiply by quantity added
          trackAddToCart(productData);
          
          // Track custom event
          trackEvent('ADD_TO_CART', productId, 'Product', { quantity, attributes });
        } else {
          trackEvent('ADD_TO_CART', productId, 'Product', { quantity, attributes });
        }
      } catch (trackingError) {
        console.warn('Meta Pixel tracking failed:', trackingError);
        trackEvent('ADD_TO_CART', productId, 'Product', { quantity, attributes });
      }
      
      toast.success('Item added to cart');
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to add item to cart';
      console.error('Error adding to cart:', error);
      dispatch({ 
        type: CART_ERROR, 
        payload: errorMsg 
      });
      toast.error(errorMsg);
      return false;
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    if (!isAuthenticated) {
      const updatedItems = loadGuestCart().map(item =>
        item._id === itemId ? { ...item, quantity } : item
      );
      saveGuestCart(updatedItems);
      dispatch({ type: UPDATE_CART_ITEM });
      dispatch({ type: SET_CART, payload: { items: filterValidCartItems(updatedItems) } });
      return true;
    }

    try {
      const response = await api.put(`/cart/${itemId}`, {
        quantity
      });

      dispatch({ 
        type: UPDATE_CART_ITEM
      });
      
      dispatch({ 
        type: SET_CART, 
        payload: {
          ...response.data.data,
          items: filterValidCartItems(response.data.data.items || [])
        }
      });
      
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to update cart';
      console.error('Error updating cart:', error);
      dispatch({ 
        type: CART_ERROR, 
        payload: errorMsg 
      });
      toast.error(errorMsg);
      return false;
    }
  };

  const removeFromCart = async (itemId) => {
    if (!isAuthenticated) {
      const updatedItems = loadGuestCart().filter(item => item._id !== itemId);
      saveGuestCart(updatedItems);
      dispatch({ type: REMOVE_CART_ITEM });
      dispatch({ type: SET_CART, payload: { items: filterValidCartItems(updatedItems) } });
      toast.success('Item removed from cart');
      return true;
    }

    try {
      const response = await api.delete(`/cart/${itemId}`);

      dispatch({ 
        type: REMOVE_CART_ITEM
      });
      
      dispatch({ 
        type: SET_CART, 
        payload: {
          ...response.data.data,
          items: filterValidCartItems(response.data.data.items || [])
        }
      });
      
      toast.success('Item removed from cart');
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to remove item from cart';
      console.error('Error removing from cart:', error);
      dispatch({ 
        type: CART_ERROR, 
        payload: errorMsg 
      });
      toast.error(errorMsg);
      return false;
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      saveGuestCart([]);
      dispatch({ type: CLEAR_CART });
      toast.success('Cart cleared');
      return true;
    }

    try {
      await api.delete('/cart');

      dispatch({ type: CLEAR_CART });
      toast.success('Cart cleared');
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to clear cart';
      console.error('Error clearing cart:', error);
      dispatch({ 
        type: CART_ERROR, 
        payload: errorMsg 
      });
      toast.error(errorMsg);
      return false;
    }
  };

  // Manual cleanup function to remove deleted products from cart
  const cleanupDeletedProducts = async () => {
    if (!isAuthenticated) {
      return false;
    }

    try {
      const currentValidItems = filterValidCartItems(state.items);
      const removedCount = state.items.length - currentValidItems.length;
      
      if (removedCount > 0) {
        // Update local state immediately
        dispatch({ 
          type: SET_CART, 
          payload: {
            items: currentValidItems
          }
        });

        // Try to clean up on server
        try {
          await cleanupCartOnServer(currentValidItems);
          toast.success(`Removed ${removedCount} unavailable item${removedCount > 1 ? 's' : ''} from cart`);
        } catch (serverError) {
          console.warn('Server cleanup failed, but local cleanup successful:', serverError);
          toast.success(`Cleaned up ${removedCount} unavailable item${removedCount > 1 ? 's' : ''} locally`);
        }
        
        return true;
      } else {
        toast.info('No invalid items found in cart');
        return false;
      }
    } catch (error) {
      console.error('Error cleaning up deleted products:', error);
      toast.error('Failed to clean up cart');
      return false;
    }
  };

  // Check if a product is in the cart
  const isInCart = (productId) => {
    return state.items.some(item => item.product && item.product._id === productId);
  };

  // Get cart item by product ID
  const getCartItem = (productId) => {
    return state.items.find(item => item.product && item.product._id === productId);
  };

  // Format price for display
  const formatPrice = (price) => {
    return price?.toLocaleString('en-IN', { 
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    });
  };

  return (
    <CartContext.Provider
      value={{
        ...state,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        isInCart,
        getCartItem,
        formatPrice,
        fetchCart,
        cleanupDeletedProducts,
        isInitialized,
        hasDeletedProducts
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    // Return default values if context is not available (e.g., outside provider)
    return defaultCartValue;
  }
  return context;
};
